import type { ApiListRequest, ApiListResponse } from "../@types/services/Api";
import type {
  DeliveryDateChangeRequest,
  DeliveryDateChangeRequestExcelRow,
  DeliveryDateChangeRequestFilterType,
  DeliveryDateChangeRequestIndex,
  DeliveryDateChangeRequestNew,
  DeliveryDateChangeRequestShow,
  ProcessedDeliveryDateChangeRequestExcelRow,
} from "../@types/services/DeliveryDateChangeRequest";
import type { PurchaseOrder } from "../@types/services/PurchaseOrder";
import type { PurchaseOrderItem } from "../@types/services/PurchaseOrderItem";
import { api } from "./api";

const API_BASE_URL = "/api/v2/delivery_date_change_requests";

export const fetchDeliveryDateChangeRequests = async ({
  filters,
  ...rest
}: ApiListRequest<DeliveryDateChangeRequestFilterType> = {}): Promise<
  ApiListResponse<DeliveryDateChangeRequestIndex>
> => {
  const response = await api.get("", {
    baseURL: API_BASE_URL,
    params: {
      ...filters,
      ...rest,
    },
  });
  return response.data;
};

export const createDeliveryDateChangeRequest = async (
  data: DeliveryDateChangeRequestNew,
): Promise<DeliveryDateChangeRequest> => {
  const response = await api.post("", data, {
    baseURL: API_BASE_URL,
  });
  return response.data;
};

export const fetchDeliveryDateChangeRequest = async (
  id: string,
): Promise<DeliveryDateChangeRequestShow> => {
  const response = await api.get(`/${id}`, {
    baseURL: API_BASE_URL,
  });
  return response.data;
};

export const approveDeliveryDateChangeRequest = async (
  id: string,
): Promise<DeliveryDateChangeRequest> => {
  const response = await api.patch(
    `/${id}/approve`,
    {},
    {
      baseURL: API_BASE_URL,
    },
  );
  return response.data;
};

export const rejectDeliveryDateChangeRequest = async (
  id: string,
  decisionJustification: string,
): Promise<DeliveryDateChangeRequest> => {
  const response = await api.patch(
    `/${id}/reject`,
    { decision_justification: decisionJustification },
    {
      baseURL: API_BASE_URL,
    },
  );
  return response.data;
};

export const processDeliveryDateChangeRequestExcelData = async (
  data: DeliveryDateChangeRequestExcelRow[],
): Promise<{ results: ProcessedDeliveryDateChangeRequestExcelRow[] }> => {
  const response = await api.post(
    "/process_excel_data",
    { data },
    {
      baseURL: API_BASE_URL,
    },
  );
  return response.data;
};

export const retrievePurchaseOrder = async (
  purchaseOrderNumber: number,
): Promise<PurchaseOrder> => {
  const response = await api.get(`/retrieve_purchase_order`, {
    baseURL: API_BASE_URL,
    params: { purchase_order_number: purchaseOrderNumber },
  });
  return response.data;
};

export const retrievePurchaseOrderItem = async (
  purchaseOrderNumber: number,
  purchaseOrderItemNumber: number,
): Promise<PurchaseOrderItem> => {
  const response = await api.get(`/retrieve_purchase_order_item`, {
    baseURL: API_BASE_URL,
    params: {
      purchase_order_item_number: purchaseOrderItemNumber,
      purchase_order_number: purchaseOrderNumber,
    },
  });
  return response.data;
};

export const retryDeliveryDateChangeRequest = async (
  id: string,
): Promise<DeliveryDateChangeRequest> => {
  const response = await api.patch(
    `/${id}/retry`,
    {},
    {
      baseURL: API_BASE_URL,
    },
  );
  return response.data;
};

export const archiveDeliveryDateChangeRequest = async (
  id: string,
): Promise<DeliveryDateChangeRequest> => {
  const response = await api.patch(
    `/${id}/archive`,
    {},
    {
      baseURL: API_BASE_URL,
    },
  );
  return response.data;
};
